// Typed wrappers around apiCall, one per endpoint in API_CONTRACT.md. Use
// these instead of calling apiCall directly so callers stay shielded from
// method/path changes.

import type {
  CampaignConfig,
  CampaignState,
  ControlAction,
  OracleDataset,
  SamplePoint,
  ScoreResponse,
  SurfaceResponse,
} from "../types";
import { apiCall } from "./client";

export type CreateCampaignResponse = { id: string; state: CampaignState };

export type ObservationItem = Partial<SamplePoint> & {
  id: string;
  outputs: Record<string, number>;
};

export type ForkRequest = {
  atIteration: number;
  overrides?: Partial<CampaignConfig>;
};

export type SurfaceParams = {
  output: string;
  xInput?: string;
  yInput?: string;
  slice?: Record<string, number>;
  resolution?: number;
};

export type ScoreParams = {
  xInput?: string;
  yInput?: string;
  slice?: Record<string, number>;
  resolution?: number;
  disabledOutputs?: string[];
};

function buildQuery(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, val] of Object.entries(params)) {
    if (val == null) continue;
    if (Array.isArray(val)) {
      if (val.length === 0) continue;
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(val.join(","))}`);
    } else if (typeof val === "object") {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(val))}`);
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(val))}`);
    }
  }
  return parts.length > 0 ? `?${parts.join("&")}` : "";
}

// ---- Oracle catalogue ------------------------------------------------------

export async function listOracles(): Promise<{ oracles: OracleDataset[] }> {
  return apiCall("/api/oracles");
}

// ---- Campaign lifecycle ----------------------------------------------------

export type CampaignSummary = {
  id: string;
  name: string;
  status: CampaignState["status"];
  iteration: number;
  pointsComplete: number;
  budget: number;
  oracleEnabled: boolean;
  oracleDatasetId?: string;
  createdAt: string;
  updatedAt: string;
};

export async function listCampaigns(): Promise<{ campaigns: CampaignSummary[] }> {
  return apiCall("/api/campaigns");
}

export async function createCampaign(
  config: CampaignConfig,
): Promise<CreateCampaignResponse> {
  return apiCall("/api/campaigns", { method: "POST", body: config });
}

export async function fetchCampaignState(id: string): Promise<CampaignState> {
  return apiCall(`/api/campaigns/${id}/state`);
}

export async function nextBatch(
  id: string,
): Promise<{ proposedPoints: SamplePoint[] }> {
  return apiCall(`/api/campaigns/${id}/next-batch`, { method: "POST", body: {} });
}

export async function postObservations(
  id: string,
  points: ObservationItem[],
): Promise<{ state: CampaignState }> {
  return apiCall(`/api/campaigns/${id}/observations`, {
    method: "POST",
    body: { points },
  });
}

export async function postManualSample(
  id: string,
  inputs: Record<string, number>,
): Promise<{ point: SamplePoint }> {
  return apiCall(`/api/campaigns/${id}/manual-sample`, {
    method: "POST",
    body: { inputs },
  });
}

export async function postControl(
  id: string,
  action: ControlAction,
): Promise<{ state: CampaignState }> {
  return apiCall(`/api/campaigns/${id}/control`, {
    method: "POST",
    body: { action },
  });
}

export async function postFork(
  id: string,
  req: ForkRequest,
): Promise<CreateCampaignResponse> {
  return apiCall(`/api/campaigns/${id}/fork`, { method: "POST", body: req });
}

// ---- Visualisation queries -------------------------------------------------

export async function fetchSurface(
  id: string,
  params: SurfaceParams,
): Promise<SurfaceResponse> {
  const q = buildQuery({
    output: params.output,
    xInput: params.xInput,
    yInput: params.yInput,
    slice: params.slice,
    resolution: params.resolution,
  });
  return apiCall(`/api/campaigns/${id}/surface${q}`);
}

export async function fetchScore(
  id: string,
  params: ScoreParams,
): Promise<ScoreResponse> {
  const q = buildQuery({
    xInput: params.xInput,
    yInput: params.yInput,
    slice: params.slice,
    resolution: params.resolution,
    disabledOutputs: params.disabledOutputs,
  });
  return apiCall(`/api/campaigns/${id}/score${q}`);
}
